// src/components/SceneShotPanel.js
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "../utils/i18n";
import {
  displayTagValue,
  getTagSelectOptions,
  normalizeTagValue,
} from "../utils/shotTags";
import { getShotDisplayNumber } from "../utils/shotNumber";
import { getSceneNumberLabel } from "../utils/parseScenes";
import {
  DEFAULT_SHOT_FRAME_FORMAT,
  getShotFrameFormat,
  DEFAULT_SHOT_COLOR_FORMAT,
  SHOT_FRAME_FORMATS,
  SHOT_COLOR_FORMATS,
  getShotColorFormat,
  getColorFormatFilter,
  effectiveFrameFormatValue,
  effectiveColorFormatValue,
} from "../utils/frameFormats";

const DEFAULT_HIGHLIGHT = "#3749BD";

const HIGHLIGHT_PRESETS = [
  "#3749BD", // blue (default)
  "#A74346", // red
  "#816C31", // yellow
  "#6F8C40", // green
  "#2C6F85", // cyan
  "#573C7C", // violet
  "#acacacff", // white/gray
  "#000000ff", // black
];

const DEFAULT_FIELD_LAYOUT = [
  { key: "shotSize", label: "Échelle", tagColor: "#2B3550", tagBold: false },
  { key: "angle", label: "Angle", tagColor: "#2B3550", tagBold: false },
  { key: "focus", label: "Focus", tagColor: "#2B3550", tagBold: false },
  { key: "framing", label: "Cadre", tagColor: "#2B3550", tagBold: false },
  { key: "movement", label: "Mouvement", tagColor: "#2B3550", tagBold: false },
  { key: "lensMm", label: "Focale", tagColor: "#2B3550", tagBold: false },
  { key: "lights", label: "Lumières", tagColor: "#2B3550", tagBold: false },
  { key: "durationEstimateSec", label: "Temps", tagColor: "#2B3550", tagBold: false },
  { key: "ratio", label: "Ratio", tagColor: "#2B3550", tagBold: false },
  { key: "colorFormat", label: "Couleur", tagColor: "#2B3550", tagBold: false },
];

const TAGGABLE_KEYS = new Set([
  "shotSize",
  "angle",
  "focus",
  "framing",
  "movement",
  "lensMm",
  "lights",
  "durationEstimateSec",
  "ratio",
  "colorFormat",
]);

const FIELD_LABEL_KEYS = {
  shotSize: "field.shotSize",
  angle: "field.angle",
  focus: "field.focus",
  framing: "field.framing",
  movement: "field.movement",
  lensMm: "field.lens",
  lights: "field.lights",
  durationEstimateSec: "field.estimation",
  ratio: "field.ratio",
  colorFormat: "field.colorFormat",
};

function getFieldLabel(t, key) {
  const k = FIELD_LABEL_KEYS[key];
  const v = k && typeof t === "function" ? t(k) : "";
  return (v || "").trim() || key;
}

function hexToRgb(hex) {
  const h = String(hex || "").replace("#", "").trim();
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return { r, g, b };
  }
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { r, g, b };
  }
  return { r: 43, g: 53, b: 80 };
}

function makeTagStyle(tagColor, tagBold) {
  const { r, g, b } = hexToRgb(tagColor || "#2B3550");

  return {
    borderColor: `rgba(${r}, ${g}, ${b}, 0.55)`,
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.20)`,

    // ✅ IMPORTANT : regular doit être vraiment regular
    fontWeight: tagBold ? 700 : 400,
  };
}

function normalizeShotFieldLayout(list, DEFAULT_FIELD_LAYOUT) {
  const provided = Array.isArray(list) ? list : [];

  const defaultsByKey = new Map(DEFAULT_FIELD_LAYOUT.map((d) => [d.key, d]));
  const out = [];
  const seen = new Set();

  const normKey = (k) => (k === "shotType" ? "angle" : k); // legacy

  // 1) keep provided order, but only known keys
  for (const raw of provided) {
    const key = normKey(raw?.key);
    if (!key || seen.has(key)) continue;
    const def = defaultsByKey.get(key);
    if (!def) continue; // drop unknown keys like "linkedShot" if still saved
    out.push({ ...def, ...raw, key });
    seen.add(key);
  }

  // 2) append missing defaults
  for (const def of DEFAULT_FIELD_LAYOUT) {
    if (seen.has(def.key)) continue;
    out.push({ ...def });
  }

  return out;
}


function ShotFieldLayoutModal({ open, onClose, layout, setLayout }) {
  const { t } = useI18n();
  if (!open) return null;

  // ✅ layout affiché = toujours complet (même si localStorage est ancien)
  const viewLayout = normalizeShotFieldLayout(layout, DEFAULT_FIELD_LAYOUT);

  const move = (from, to) => {
    setLayout((prev) => {
      const base = normalizeShotFieldLayout(prev ?? viewLayout, DEFAULT_FIELD_LAYOUT);
      if (from < 0 || to < 0 || from >= base.length || to >= base.length) return base;

      const arr = [...base];
      const [it] = arr.splice(from, 1);
      arr.splice(to, 0, it);
      return arr;
    });
  };

  const update = (idx, patch) => {
    setLayout((prev) => {
      const base = normalizeShotFieldLayout(prev ?? viewLayout, DEFAULT_FIELD_LAYOUT);
      return base.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    });
  };

  return (
    <div className="modalOverlay" onMouseDown={onClose}>
      <div className="modalPanel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modalHeader">
          <div className="modalTitle">
            {t?.("shotFields.modal.title") || "Champs du Shot Editor"}
          </div>
          <button className="modalCloseButton" type="button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modalBody">
          <div className="helpText">
            {t?.("shotFields.modal.help") ||
              "Réorganise l’ordre des champs (global) et personnalise le style des tags (couleur + gras)."}
          </div>

            <div className="fieldOrderList">
              {viewLayout.map((it, idx) => {
                const displayName = getFieldLabel(t, it.key);

                return (
                  <div
                    key={it.key}
                    className={`fieldOrderRow ${it.hidden ? "isHidden" : ""}`}
                  >
                    <div className="fieldOrderLeft">
                      <div className="fieldOrderName">{displayName}</div>
                      {/* ✅ on supprime la ligne qui affichait la key pour éviter “2 titres” */}
                      {/* <div className="fieldOrderKey">{it.key}</div> */}
                    </div>

                    <div className="fieldOrderControls">
                      <button
                        className="btn btnGhost"
                        type="button"
                        disabled={idx === 0}
                        onClick={() => move(idx, idx - 1)}
                        title="Monter"
                      >
                        ↑
                      </button>

                      <button
                        className="btn btnGhost"
                        type="button"
                        disabled={idx === viewLayout.length - 1}
                        onClick={() => move(idx, idx + 1)}
                        title="Descendre"
                      >
                        ↓
                      </button>

                      <input
                        className="fieldColor"
                        type="color"
                        value={it.tagColor || "#2B3550"}
                        onChange={(e) => update(idx, { tagColor: e.target.value })}
                        title="Couleur du tag"
                      />

                      <label className="fieldBold">
                        <input
                          type="checkbox"
                          checked={!!it.tagBold}
                          onChange={(e) => update(idx, { tagBold: e.target.checked })}
                        />
                        Bold
                      </label>

                      <button
                        className={`btn btnGhost fieldEyeBtn ${it.hidden ? "isOff" : ""}`}
                        type="button"
                        onClick={() => update(idx, { hidden: !it.hidden })}
                        aria-pressed={it.hidden ? "true" : "false"}
                        title={
                          it.hidden
                            ? t?.("shotFields.show") || "Afficher le tag dans la liste des plans"
                            : t?.("shotFields.hide") || "Masquer le tag dans la liste des plans"
                        }
                      >
                        {it.hidden ? "🚫" : "👁"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>


          <div className="modalFooter">
            <button
              className="btn btnGhost"
              type="button"
              onClick={() => setLayout([...DEFAULT_FIELD_LAYOUT])}
            >
                {t?.("common.reset") || "Réinitialiser"}
              </button> 

            <button className="btn btnPrimary" type="button" onClick={onClose}>
              {t?.("common.close") || "Fermer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SceneShotPanel(props = {}) {
  const {
    scriptText,
    scenes,
    shots,
    selectedSceneId,
    setSelectedSceneId,
    onAddShotFromSelection,
    onAddShotWithoutText,
    editorInScriptPanel,
    selectedShotId,
    setSelectedShotId,
    onUpdateShot,
    onUpdateShotTextFromSelection,
    scriptViewMode,
    sceneSelection,
    scriptSelection,
    onDeleteShot,
    onClearShotText,
    onSetShotNumber,
    onReorderShotByDrag,
    onMoveShotStep,
    linkedLetterReorder,
    onLinkShots,
    onUnlinkShot,
    onUpdateScene,
    organizeMode,
    setOrganizeMode,
    onDeleteShots,
    onMoveShotsToSceneId,
    onReorderShotsInScene,
    onApplyShotOrder,
    onReorderScenes,
    onSetSceneDeleted,
    onRenameScene,
    includeLinkedShotsInCounts,
    shotPrefixMode,
    shotEditorTermsLang,
    shotFieldLayout,
    setShotFieldLayout,
  } = props;
  const shotFrameFormat = props.shotFrameFormat || DEFAULT_SHOT_FRAME_FORMAT;

  const { lang, t } = useI18n();

  // ---------
  // Lang des tags (labels FR/EN) dans le Shot Editor
  // ---------
  const tagsLang = useMemo(() => {
    const v = String(shotEditorTermsLang || "auto").toLowerCase();
    if (v === "fr" || v === "en") return v;
    return lang; // auto => suit l’UI
  }, [shotEditorTermsLang, lang]);

  // ---------
  // Field layout (ordre + style tags) : merge safe
  // ---------
  const effectiveFieldLayout = useMemo(() => {
    const provided = Array.isArray(shotFieldLayout) && shotFieldLayout.length
      ? shotFieldLayout
      : null;

    const defaultsByKey = new Map(DEFAULT_FIELD_LAYOUT.map((d) => [d.key, d]));
    const out = [];
    const seen = new Set();

    const normalizeKey = (k) => (k === "shotType" ? "angle" : k); // ✅ migration legacy

    // 1) on reprend l’ordre CUSTOM en premier
    if (provided) {
      for (const raw of provided) {
        const key = normalizeKey(raw?.key);
        if (!key) continue;
        if (!defaultsByKey.has(key)) continue;
        if (seen.has(key)) continue;

        out.push({ ...defaultsByKey.get(key), ...raw, key }); // key normalisée
        seen.add(key);
      }
    }

    // 2) on ajoute le reste des champs manquants (pour éviter de “perdre” des champs)
    for (const d of DEFAULT_FIELD_LAYOUT) {
      if (seen.has(d.key)) continue;
      out.push({ ...d });
    }

    return out;
  }, [shotFieldLayout]);

  const fieldIndex = useMemo(() => {
    const m = new Map();
    effectiveFieldLayout.forEach((it, i) => m.set(it.key, i));
    return m;
  }, [effectiveFieldLayout]);

  // ✅ Layout priority: keep SceneDetail width first
  const sceneDetailRef = useRef(null);
  const sceneTitleRowRef = useRef(null);
  const didCaptureSceneDetailW = useRef(false);

  const shotItemRefs = useRef(new Map());
  const shotsWrapRef = useRef(null);

  const naturalSceneDetailW = useRef(0);

  useEffect(() => {
    const el = sceneDetailRef.current;
    if (!el) return;

    const root = document.documentElement;

    const applyVars = () => {
      // Mesure la largeur naturelle du bloc une seule fois (évite toute boucle de rétroaction).
      let w = naturalSceneDetailW.current;
      if (!w) {
        w = Math.round(el.getBoundingClientRect().width || 0);
        if (!w) return;
        naturalSceneDetailW.current = w;
        didCaptureSceneDetailW.current = true;
      }

      // ✅ Bornage sur la fenêtre pour que les 2 panneaux tiennent sans scroll horizontal.
      const panel1Min = 320; // doit rester cohérent avec --panel1MinW (CSS)
      const gaps = 40; // gap grille + paddings
      const viewportCap = Math.max(
        520,
        Math.floor((window.innerWidth || 1440) - panel1Min - gaps)
      );

      const sceneDetailMin = Math.min(w, Math.max(360, viewportCap - 260));
      const panel2Min = Math.min(w + 260, viewportCap);

      root.style.setProperty("--sceneDetailMinW", `${Math.round(sceneDetailMin)}px`);
      root.style.setProperty("--panel2MinW", `${Math.round(panel2Min)}px`);
    };

    const raf = requestAnimationFrame(applyVars);
    window.addEventListener("resize", applyVars);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", applyVars);
    };
  }, [scriptText, scenes.length]);

  // ---------------------------
  // Display mode
  // ---------------------------
  const [shotDisplayMode, setShotDisplayMode] = useState("classic"); // "classic" | "compact" | "big"

  // ---------------------------
  // Organisation (mode organisation : scènes / plans)
  // ---------------------------
  const [orgTarget, setOrgTarget] = useState(null); // null | "shots" | "scenes"
  const [orgShotIds, setOrgShotIds] = useState([]);
  const [orgSceneIds, setOrgSceneIds] = useState([]);
  const [orgMultiShots, setOrgMultiShots] = useState(false);
  const [orgMultiScenes, setOrgMultiScenes] = useState(false);
  const [moveTargetSceneId, setMoveTargetSceneId] = useState(null); // destination pour déplacer des plans
  const [orgDeleteConfirm, setOrgDeleteConfirm] = useState(null); // { kind: "shots"|"scenes", count }
  const [renameSceneId, setRenameSceneId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [renumberOpen, setRenumberOpen] = useState(false);
  const [renumberSceneId, setRenumberSceneId] = useState(null);
  const [renumberOrder, setRenumberOrder] = useState([]); // ids ordonnés

  // Entrée/sortie du mode organisation.
  useEffect(() => {
    if (!organizeMode) {
      // sortie : on réinitialise tout l'état d'organisation
      setOrgTarget(null);
      setOrgShotIds([]);
      setOrgSceneIds([]);
      setOrgMultiShots(false);
      setOrgMultiScenes(false);
      setMoveTargetSceneId(null);
      setOrgDeleteConfirm(null);
      setRenameSceneId(null);
      setRenumberOpen(false);
      return;
    }
    // entrée : si un plan est déjà sélectionné, on affiche directement ses outils
    if (selectedShotId) {
      setOrgTarget("shots");
      setOrgShotIds([selectedShotId]);
    }
  }, [organizeMode]);

  const selectOrgShot = (shotId) => {
    setOrgTarget("shots");
    setOrgSceneIds([]);
    setMoveTargetSceneId(null);
    setSelectedShotId?.(shotId);
    setOrgShotIds((prev) =>
      orgMultiShots
        ? prev.includes(shotId)
          ? prev.filter((x) => x !== shotId)
          : [...prev, shotId]
        : [shotId]
    );
  };

  const selectOrgScene = (sceneId) => {
    setOrgTarget("scenes");
    setMoveTargetSceneId(null);
    setOrgSceneIds((prev) =>
      orgMultiScenes
        ? prev.includes(sceneId)
          ? prev.filter((x) => x !== sceneId)
          : [...prev, sceneId]
        : [sceneId]
    );
  };

  const handleShotOrgClick = (shotId) => {
    // Plus de disclaimer : cliquer un plan bascule directement en organisation des plans.
    selectOrgShot(shotId);
  };

  const handleSceneOrgClick = (sceneId) => {
    // Si des plans sont sélectionnés : la scène cliquée devient la destination
    // de déplacement (bouton dédié), sans changer la sélection de plans.
    if (orgTarget === "shots" && orgShotIds.length > 0) {
      setMoveTargetSceneId((prev) => (prev === sceneId ? null : sceneId));
      return;
    }
    selectOrgScene(sceneId);
  };

  // Actions de la barre d'organisation
  const orgRequestDeleteShots = () => {
    if (orgShotIds.length === 0) return;
    setOrgDeleteConfirm({ kind: "shots", count: orgShotIds.length });
  };
  const orgMoveShotsToTarget = () => {
    if (orgShotIds.length === 0 || !moveTargetSceneId) return;
    onMoveShotsToSceneId?.(orgShotIds, moveTargetSceneId);
    setOrgShotIds([]);
    setMoveTargetSceneId(null);
    setOrgTarget(null);
    setOrganizeMode?.(false); // le déplacement de plans quitte le mode organisation
  };
  const orgMoveScenes = (dir) => {
    if (orgSceneIds.length > 0) onReorderScenes?.(orgSceneIds, dir);
  };
  const orgRequestToggleDeleteScenes = () => {
    if (orgSceneIds.length === 0) return;
    const map = new Map(scenes.map((s) => [s.id, s]));
    const willDelete = orgSceneIds.some((id) => !map.get(id)?.deleted);
    if (!willDelete) {
      // Restauration : pas de disclaimer nécessaire.
      orgSceneIds.forEach((id) => onSetSceneDeleted?.(id, false));
      setOrgSceneIds([]);
      setOrgTarget(null);
      return;
    }
    setOrgDeleteConfirm({ kind: "scenes", count: orgSceneIds.length });
  };
  const orgConfirmDelete = () => {
    if (!orgDeleteConfirm) return;
    if (orgDeleteConfirm.kind === "shots") {
      onDeleteShots?.(orgShotIds);
      setOrgShotIds([]);
    } else {
      orgSceneIds.forEach((id) => onSetSceneDeleted?.(id, true));
      setOrgSceneIds([]);
    }
    setOrgTarget(null);
    setOrgDeleteConfirm(null);
  };
  const orgStartRename = () => {
    const id = orgSceneIds[0];
    if (!id) return;
    const sc = scenes.find((s) => s.id === id);
    setRenameDraft(sc?.title || "");
    setRenameSceneId(id);
  };
  const commitRename = () => {
    if (renameSceneId) onRenameScene?.(renameSceneId, renameDraft.trim());
    setRenameSceneId(null);
  };

  // Réordonne d'un cran (haut/bas) les plans sélectionnés dans leur scène.
  const orgReorderShots = (dir) => {
    if (orgShotIds.length === 0) return;
    const first = shots.find((s) => orgShotIds.includes(s.id));
    const sceneId = first?.sceneId;
    if (sceneId) onReorderShotsInScene?.(sceneId, orgShotIds, dir);
  };

  // Ouvre la fenêtre de renumérotation des plans de la scène sélectionnée.
  const orgOpenRenumber = () => {
    const first = shots.find((s) => orgShotIds.includes(s.id));
    const sceneId = first?.sceneId;
    if (!sceneId) return;
    const ids = shots
      .filter((s) => s.sceneId === sceneId)
      .slice()
      .sort((a, b) => shotListKey(a) - shotListKey(b))
      .map((s) => s.id);
    setRenumberSceneId(sceneId);
    setRenumberOrder(ids);
    setRenumberOpen(true);
  };
  const renumberMove = (idx, dir) => {
    setRenumberOrder((prev) => {
      const j = dir === "up" ? idx - 1 : idx + 1;
      if (j < 0 || j >= prev.length) return prev;
      const arr = prev.slice();
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };
  const renumberApply = () => {
    if (renumberSceneId) onApplyShotOrder?.(renumberSceneId, renumberOrder);
    setRenumberOpen(false);
  };

  const getShotImageSrc = (shot) => {
    if (!shot?.image) return "";
    if (typeof shot.image === "string") return shot.image;
    return shot.image.dataUrl || shot.image.url || shot.image.src || "";
  };

  // ---------------------------
  // Scene derived
  // ---------------------------
  const selectedScene = scenes.find((s) => s.id === selectedSceneId) || null;

  // Réglages par scène (ratio / format couleur) avec repli sur le réglage global
  const sceneById = useMemo(() => {
    const m = new Map();
    for (const s of scenes) m.set(s.id, s);
    return m;
  }, [scenes]);

  const sceneEffFrameFormat = (scene) =>
    scene?.frameFormat || shotFrameFormat || DEFAULT_SHOT_FRAME_FORMAT;
  const sceneEffColorFormat = (scene) => scene?.colorFormat || DEFAULT_SHOT_COLOR_FORMAT;

  const shotFrameCssRatio = useMemo(
    () => getShotFrameFormat(selectedScene?.frameFormat || shotFrameFormat || DEFAULT_SHOT_FRAME_FORMAT).css,
    [selectedScene, shotFrameFormat]
  );
  const selectedSceneIndex = useMemo(() => {
    if (!selectedScene) return -1;
    return scenes.findIndex((s) => s.id === selectedScene.id);
  }, [scenes, selectedScene]);

  const displaySceneTitle = useMemo(() => {
    if (!selectedScene) return "";
    const label = getSceneNumberLabel(selectedScene, selectedSceneIndex);
    const baseTitle = selectedScene.title?.trim() || t("fallback.scene", { n: label });

    const alreadyPrefixed =
      /^SC\s*\d+[A-Za-z]?\b[.:\-]?/i.test(baseTitle) ||
      /^(SCENE|SCÈNE)\s*\d+[A-Za-z]?\b/i.test(baseTitle);

    return alreadyPrefixed ? baseTitle : `SC${label}. ${baseTitle}`;
  }, [selectedScene, selectedSceneIndex, t]);

  const shotListKey = (s) =>
    Number.isFinite(s?.listOrder)
      ? s.listOrder
      : Number.isFinite(s?.order)
      ? s.order
      : s?.scriptRange?.start ?? 0;

  const shotsForSelectedScene = useMemo(() => {
    if (!selectedScene) return [];
    return shots
      .filter((s) => s.sceneId === selectedScene.id)
      .slice()
      .sort((a, b) => shotListKey(a) - shotListKey(b));
  }, [shots, selectedScene]);

  // Drag & drop de la liste des plans
  const [dragShotId, setDragShotId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  const shotsBySceneId = useMemo(() => {
    const map = new Map();

    if (includeLinkedShotsInCounts) {
      for (const sh of shots) {
        map.set(sh.sceneId, (map.get(sh.sceneId) ?? 0) + 1);
      }
      return map;
    }

    const seenByScene = new Map(); // sceneId -> Set(groupKey)
    for (const sh of shots) {
      const sceneId = sh.sceneId;
      const groupKey = sh.linkGroupId || sh.id;

      let set = seenByScene.get(sceneId);
      if (!set) {
        set = new Set();
        seenByScene.set(sceneId, set);
      }

      if (set.has(groupKey)) continue;
      set.add(groupKey);

      map.set(sceneId, (map.get(sceneId) ?? 0) + 1);
    }

    return map;
  }, [shots, includeLinkedShotsInCounts]);

  const selectedSceneShotsCount = useMemo(() => {
    if (includeLinkedShotsInCounts) return shotsForSelectedScene.length;

    const set = new Set();
    for (const sh of shotsForSelectedScene) {
      set.add(sh.linkGroupId || sh.id);
    }
    return set.size;
  }, [shotsForSelectedScene, includeLinkedShotsInCounts]);

  // ---------------------------
  // Selected shot derived
  // ---------------------------
  const selectedShot = shots.find((s) => s.id === selectedShotId) || null;

  const selectedShotText = useMemo(() => {
    if (!selectedShot) return "";
    return scriptText.slice(selectedShot.scriptRange.start, selectedShot.scriptRange.end);
  }, [scriptText, selectedShot]);

  // ---------------------------
  // Add shot
  // ---------------------------
  const hasSceneSelection =
    Math.abs(((sceneSelection?.end ?? 0) - (sceneSelection?.start ?? 0)) || 0) > 0;

  const hasScriptSelection =
    Math.abs(((scriptSelection?.end ?? 0) - (scriptSelection?.start ?? 0)) || 0) > 0;

  const canAddShot = Boolean(selectedScene) && scriptViewMode === "scene" && hasSceneSelection;

  const handleAddShot = () => {
    if (!canAddShot) return;
    onAddShotFromSelection?.({
      sceneId: selectedScene.id,
      selStart: sceneSelection.start,
      selEnd: sceneSelection.end,
    });
  };

  // Ajout d'un plan sans sélection de texte (insert)
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const addMenuRef = useRef(null);

  const handleAddShotNoText = () => {
    if (!selectedScene) return;
    onAddShotWithoutText?.({ sceneId: selectedScene.id });
    setIsAddMenuOpen(false);
  };

  useEffect(() => {
    if (!isAddMenuOpen) return;
    const onDown = (e) => {
      if (e.key === "Escape") setIsAddMenuOpen(false);
    };
    const onClickOutside = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setIsAddMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("mousedown", onClickOutside);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("mousedown", onClickOutside);
    };
  }, [isAddMenuOpen]);

  // ---------------------------
  // Display mode menu
  // ---------------------------
  const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
  const modeMenuRef = useRef(null);

  const DISPLAY_MODES = useMemo(
    () => ({
      classic: { label: t("mode.classic.title"), sub: t("mode.classic.sub") },
      compact: { label: t("mode.compact.title"), sub: t("mode.compact.sub") },
      big: { label: t("mode.big.title"), sub: t("mode.big.sub") },
    }),
    [t]
  );

  useEffect(() => {
    if (!isModeMenuOpen) return;

    const onDown = (e) => {
      if (e.key === "Escape") setIsModeMenuOpen(false);
    };

    const onClickOutside = (e) => {
      if (!modeMenuRef.current) return;
      if (!modeMenuRef.current.contains(e.target)) setIsModeMenuOpen(false);
    };

    window.addEventListener("keydown", onDown);
    window.addEventListener("mousedown", onClickOutside);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("mousedown", onClickOutside);
    };
  }, [isModeMenuOpen]);

  // ---------------------------
  // Selection logic for Update text
  // ---------------------------
  const selectionGlobal = useMemo(() => {
    if (!selectedScene) return null;

    if (scriptViewMode === "scene") {
      const a = Math.min(sceneSelection?.start ?? 0, sceneSelection?.end ?? 0);
      const b = Math.max(sceneSelection?.start ?? 0, sceneSelection?.end ?? 0);
      if (b - a <= 0) return null;
      return { start: selectedScene.start + a, end: selectedScene.start + b };
    }

    const a = Math.min(scriptSelection?.start ?? 0, scriptSelection?.end ?? 0);
    const b = Math.max(scriptSelection?.start ?? 0, scriptSelection?.end ?? 0);
    if (b - a <= 0) return null;
    return { start: a, end: b };
  }, [scriptViewMode, sceneSelection, scriptSelection, selectedScene]);

  const selectionWithinSelectedScene = useMemo(() => {
    if (!selectedScene || !selectionGlobal) return false;
    return (
      selectionGlobal.start >= selectedScene.start &&
      selectionGlobal.end <= selectedScene.end
    );
  }, [selectedScene, selectionGlobal]);

  const selectionKey = selectionGlobal
    ? `${selectionGlobal.start}-${selectionGlobal.end}`
    : "";

  const canModifyText =
    Boolean(selectedShot) &&
    Boolean(selectedScene) &&
    Boolean(selectionGlobal) &&
    selectionWithinSelectedScene &&
    selectedShot?.sceneId === selectedScene?.id &&
    ((scriptViewMode === "scene" && hasSceneSelection) ||
      (scriptViewMode === "full" && hasScriptSelection));

  const [confirmTextUpdate, setConfirmTextUpdate] = useState(null);
  const isConfirmingTextUpdate =
    !!confirmTextUpdate &&
    !!selectedShot &&
    confirmTextUpdate.shotId === selectedShot.id &&
    confirmTextUpdate.selectionKey === selectionKey &&
    canModifyText;

  const handleModifyText = () => {
    if (!canModifyText) return;

    if (isConfirmingTextUpdate) {
      onUpdateShotTextFromSelection?.(selectedShot.id, selectionGlobal);
      setConfirmTextUpdate(null);
      return;
    }

    setConfirmDeleteShot(null);
    setConfirmResetShot(null);

    setConfirmTextUpdate({
      shotId: selectedShot.id,
      selectionKey,
    });
  };

  // ---------------------------
  // Delete / Reset confirmation
  // ---------------------------
  const [confirmDeleteShot, setConfirmDeleteShot] = useState(null);
  const [confirmResetShot, setConfirmResetShot] = useState(null);
  const [confirmClearText, setConfirmClearText] = useState(null);

  const isConfirmingClearText =
    !!confirmClearText && !!selectedShot && confirmClearText.shotId === selectedShot.id;

  const handleClearText = () => {
    if (!selectedShot) return;
    if (isConfirmingClearText) {
      onClearShotText?.(selectedShot.id);
      setConfirmClearText(null);
      return;
    }
    setConfirmTextUpdate(null);
    setConfirmResetShot(null);
    setConfirmDeleteShot(null);
    setConfirmClearText({ shotId: selectedShot.id });
  };

  // ---- Réordonner le plan sélectionné via les boutons ▲▼ (#4) ----
  const [reorderConfirm, setReorderConfirm] = useState(null); // { apply } -> resync master/-A
  const [sautConfirm, setSautConfirm] = useState(null); // { applyYes } -> plan lié au-dessus du -A

  // Le resync complet + confirmation ne concernent qu'un plan master ou lié « -A ».
  const shotNeedsResync = (shot) => {
    if (!shot) return false;
    if (shot.isMaster) return true;
    return /-A$/i.test(String(shot.shotNumber || ""));
  };

  // Membre « ancre » (-A) d'un groupe lié.
  const groupAnchorOf = (shot) => {
    if (!shot?.linkGroupId) return null;
    const members = shotsForSelectedScene.filter((s) => s.linkGroupId === shot.linkGroupId);
    if (members.length < 2) return null;
    return members.find((s) => /-A$/i.test(String(s.shotNumber || ""))) || members[0];
  };

  // Simule le nouvel ordre visuel (liste d'ids) après un déplacement.
  const simulateOrderIds = (shotId, kind, arg) => {
    const ids = shotsForSelectedScene.map((s) => s.id);
    const i = ids.indexOf(shotId);
    if (i < 0) return null;

    if (kind === "step") {
      const j = arg === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= ids.length) return null;
      const arr = ids.slice();
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    }
    // drop : arg = targetId
    const arr = ids.slice();
    const from = arr.indexOf(shotId);
    const origT = arr.indexOf(arg);
    if (origT < 0) return null;
    arr.splice(from, 1);
    let to = arr.indexOf(arg);
    if (to < 0) to = arr.length;
    if (from < origT) to += 1;
    arr.splice(to, 0, shotId);
    return arr;
  };

  const willGoAboveAnchor = (shot, newIds) => {
    const anchor = groupAnchorOf(shot);
    if (!anchor || anchor.id === shot.id || !newIds) return false;
    return newIds.indexOf(shot.id) < newIds.indexOf(anchor.id);
  };

  // Aiguillage central d'un déplacement de plan (kind = "step"|"drop").
  const dispatchReorder = (shot, kind, arg) => {
    if (!shot || !selectedScene) return;
    const call = (opts) => {
      if (kind === "step") onMoveShotStep?.(selectedScene.id, shot.id, arg, opts);
      else onReorderShotByDrag?.(selectedScene.id, shot.id, arg, opts);
    };

    // Master / -A : resync complet (avec confirmation).
    if (shotNeedsResync(shot)) {
      setReorderConfirm({ apply: () => call({ resyncBase: true }) });
      return;
    }

    // Plan lié + réglage actif : les lettres suivent l'ordre, sauf passage au-dessus du -A.
    if (shot.linkGroupId && linkedLetterReorder) {
      const newIds = simulateOrderIds(shot.id, kind, arg);
      if (willGoAboveAnchor(shot, newIds)) {
        const anchor = groupAnchorOf(shot);
        setSautConfirm({
          applyYes: () => call({ becomeAnchorId: shot.id, oldAnchorId: anchor?.id }),
        });
        return;
      }
      call({});
      return;
    }

    // Plan normal (ou réglage désactivé) : déplacement purement visuel.
    call({});
  };

  const confirmMoveSelectedShot = () => {
    const apply = reorderConfirm?.apply;
    setReorderConfirm(null);
    if (typeof apply === "function") apply();
  };

  const confirmSautYes = () => {
    const apply = sautConfirm?.applyYes;
    setSautConfirm(null);
    if (typeof apply === "function") apply();
  };

  const isConfirmingDelete =
    !!confirmDeleteShot && !!selectedShot && confirmDeleteShot.shotId === selectedShot.id;

  const isConfirmingReset =
    !!confirmResetShot && !!selectedShot && confirmResetShot.shotId === selectedShot.id;

  const handleDeleteShot = () => {
    if (!selectedShot) return;

    if (isConfirmingDelete) {
      onDeleteShot?.(selectedShot.id);
      setConfirmDeleteShot(null);
      return;
    }

    setConfirmTextUpdate(null);
    setConfirmResetShot(null);
    setConfirmDeleteShot({ shotId: selectedShot.id });
  };

  const handleResetShot = () => {
    if (!selectedShot) return;

    if (isConfirmingReset) {
      onUpdateShot?.(selectedShot.id, {
        // reset uniquement les champs du Shot Editor
        description: "",
        shotSize: "",
        angle: "",
        shotType: "", // legacy compat
        focus: "",
        framing: "",
        movement: "",
        lensMm: "",
        lights: "",
        durationEstimateSec: 0,
        highlightColor: "",
        isMaster: false,
        frameFormat: "",
        colorFormat: "",
      });

      setConfirmResetShot(null);
      return;
    }

    setConfirmTextUpdate(null);
    setConfirmDeleteShot(null);
    setConfirmResetShot({ shotId: selectedShot.id });
  };

  // Reset confirmations on context changes
  useEffect(() => {
    setConfirmTextUpdate(null);
    setConfirmDeleteShot(null);
    setConfirmResetShot(null);
    setConfirmClearText(null);
  }, [selectedShotId, selectedSceneId, scriptViewMode, selectionKey]);

  // ---------------------------
  // Thumb image import/remove
  // ---------------------------
  const thumbImageInputRef = useRef(null);
  const pendingThumbShotIdRef = useRef(null);

  const openThumbImagePicker = (shotId, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    pendingThumbShotIdRef.current = shotId;

    const input = thumbImageInputRef.current;
    if (!input) return;

    input.value = "";
    input.click();
  };

  const onThumbImagePicked = (e) => {
    const shotId = pendingThumbShotIdRef.current;
    const file = e.target.files?.[0];

    if (!shotId || !file) {
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      onUpdateShot?.(shotId, {
        image: dataUrl ? { dataUrl, name: file.name, type: file.type } : null,
      });
    };
    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const removeThumbImage = (shotId, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onUpdateShot?.(shotId, { image: null });
  };

  // ---------------------------
  // Tags + preview helpers (localized)
  // ---------------------------
  const getTagValueForShot = (key, shot) => {
    if (!shot) return "";

    switch (key) {
      case "shotSize": {
        const v = String(shot.shotSize || "").trim();
        return v ? displayTagValue("shotSize", v, tagsLang) : "";
      }

      case "angle": {
        const v = String(shot.angle || shot.shotType || "").trim();
        return v ? displayTagValue("angle", v, tagsLang) : "";
      }

      case "focus": {
        const v = String(shot.focus || "").trim();
        return v ? displayTagValue("focus", v, tagsLang) : "";
      }

      case "framing": {
        const v = String(shot.framing || "").trim();
        return v ? displayTagValue("framing", v, tagsLang) : "";
      }

      case "movement": {
        const v = String(shot.movement || "").trim();
        return v ? displayTagValue("movement", v, tagsLang) : "";
      }

      case "lensMm": {
        const raw = String(shot.lensMm ?? "").trim();
        if (!raw) return "";
        const isNumeric = /^\d+(\.\d+)?$/.test(raw);
        return isNumeric ? `${raw}mm` : raw;
      }

      case "durationEstimateSec": {
        const dur = Number(shot.durationEstimateSec ?? 0);
        return dur > 0 ? `${dur}s` : "";
      }

      case "lights": {
        const v = String(shot?.lights ?? "").trim();
        return v ? v : "";
      }

      case "ratio": {
        const scene = sceneById.get(shot.sceneId) || selectedScene;
        const fmt = effectiveFrameFormatValue(shot, scene, shotFrameFormat);
        return getShotFrameFormat(fmt).short || "";
      }

      case "colorFormat": {
        const scene = sceneById.get(shot.sceneId) || selectedScene;
        const cf = effectiveColorFormatValue(shot, scene);
        return t(getShotColorFormat(cf).labelKey);
      }

      default:
        return "";
    }
  };

  const getShotInfoTagNodes = (shot) => {
    const nodes = [];

    // ✅ #1 : le tag MASTER apparaît en premier
    if (shot?.isMaster) {
      nodes.push(
        <span key={`${shot.id}-tag-master`} className="shotTag shotTag--master">
          ★ {t("shotEditor.master")}
        </span>
      );
    }

    for (const it of effectiveFieldLayout) {
      if (!TAGGABLE_KEYS.has(it.key)) continue;
      if (it.hidden) continue; // tag masqué depuis la fenêtre « Champs »

      const value = getTagValueForShot(it.key, shot);
      if (!value) continue;

      nodes.push(
        <span
          key={`${shot.id}-tag-${it.key}`}
          className="shotTag"
          style={makeTagStyle(it.tagColor, it.tagBold)}
        >
          {value}
        </span>
      );
    }

    return nodes;
  };

  const getShotDescriptionLine = (shot) => {
    const d = (shot?.description || "").trim();
    return d.length ? d : "—";
  };

  const getAssignedTextPreview = (shot) => {
    const raw = scriptText.slice(shot.scriptRange.start, shot.scriptRange.end);
    const trimmed = raw.trim();
    if (!trimmed) return "";
    const max = 280;
    return trimmed.length > max ? trimmed.slice(0, max) + "…" : trimmed;
  };

  // ---------------------------
  // Duration totals
  // ---------------------------
  const durationBySceneId = useMemo(() => {
    const map = new Map();
    for (const sh of shots) {
      const v = Number(sh.durationEstimateSec ?? 0);
      map.set(sh.sceneId, (map.get(sh.sceneId) ?? 0) + (Number.isFinite(v) ? v : 0));
    }
    return map;
  }, [shots]);

  const formatDuration = (totalSec) => {
    const s = Math.max(0, Math.round(Number(totalSec) || 0));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}m ${r.toString().padStart(2, "0")}s`;
  };

  // Scènes barrées (supprimées visuellement) : exclues des totaux et de la shotlist.
  const deletedSceneIds = useMemo(
    () => new Set(scenes.filter((s) => s.deleted).map((s) => s.id)),
    [scenes]
  );

  const totalShots = useMemo(() => {
    const activeShots = shots.filter((sh) => !deletedSceneIds.has(sh.sceneId));
    if (includeLinkedShotsInCounts) return activeShots.length;

    let total = 0;
    const seenByScene = new Map();

    for (const sh of activeShots) {
      const sceneId = sh.sceneId;
      const groupKey = sh.linkGroupId || sh.id;

      let set = seenByScene.get(sceneId);
      if (!set) {
        set = new Set();
        seenByScene.set(sceneId, set);
      }

      if (set.has(groupKey)) continue;
      set.add(groupKey);
      total += 1;
    }

    return total;
  }, [shots, includeLinkedShotsInCounts, deletedSceneIds]);

  const totalScenes = useMemo(
    () => scenes.filter((s) => !s.deleted).length,
    [scenes]
  );

  const totalDurationSec = useMemo(() => {
    return shots.reduce((acc, sh) => {
      if (deletedSceneIds.has(sh.sceneId)) return acc;
      const v = Number(sh.durationEstimateSec ?? 0);
      return acc + (Number.isFinite(v) ? v : 0);
    }, 0);
  }, [shots, deletedSceneIds]);

  // ---------------------------
  // Totals layout: measure overflow (si tu l'utilises côté CSS)
  // ---------------------------
  const totalsWrapRef = useRef(null);
  const totalsMeasureRef = useRef(null);
  const [totalsStacked, setTotalsStacked] = useState(false);

  useLayoutEffect(() => {
    const wrap = totalsWrapRef.current;
    const measure = totalsMeasureRef.current;
    if (!wrap || !measure) return;

    const mq = window.matchMedia("(max-width: 1919px)");

    const check = () => {
      if (mq.matches) {
        setTotalsStacked(true);
        return;
      }

      const items = measure.querySelectorAll(".sceneTotalsItem");
      let overflow = false;

      for (const it of items) {
        if (it.scrollWidth > it.clientWidth + 1) {
          overflow = true;
          break;
        }
      }

      setTotalsStacked((prev) => (prev === overflow ? prev : overflow));
    };

    check();

    const ro = new ResizeObserver(check);
    ro.observe(wrap);

    const onResize = () => check();
    window.addEventListener("resize", onResize);

    const onMqChange = () => check();
    if (mq.addEventListener) mq.addEventListener("change", onMqChange);
    else mq.addListener(onMqChange);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      if (mq.removeEventListener) mq.removeEventListener("change", onMqChange);
      else mq.removeListener(onMqChange);
    };
  }, [lang, totalShots, totalScenes, totalDurationSec]);

  // ------------------------------
  // Panneau fixe Shot editor
  // ------------------------------
  const [isShotEditorOpen, setIsShotEditorOpen] = useState(true);

  // Shot Editor déporté dans le panneau scénario (#3) : trouve le conteneur hôte.
  const [editorHost, setEditorHost] = useState(null);
  useEffect(() => {
    if (!editorInScriptPanel) {
      setEditorHost(null);
      return;
    }
    let raf = 0;
    let tries = 0;
    const tick = () => {
      const el = document.getElementById("scriptEditorHost");
      if (el) {
        setEditorHost(el);
        return;
      }
      if (tries++ < 40) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [editorInScriptPanel]);

  // ---------------------------
  // Select options (localized labels, canonical values)
  // ---------------------------
  const shotSizeOptions = useMemo(() => getTagSelectOptions("shotSize", tagsLang), [tagsLang]);
  const angleOptions   = useMemo(() => getTagSelectOptions("angle", tagsLang),   [tagsLang]);
  const focusOptions   = useMemo(() => getTagSelectOptions("focus", tagsLang),   [tagsLang]);
  const framingOptions = useMemo(() => getTagSelectOptions("framing", tagsLang), [tagsLang]);
  const movementOptions= useMemo(() => getTagSelectOptions("movement", tagsLang),[tagsLang]);

  // ---------------------------
  // Linked shots
  // ---------------------------
  const linkedShotOptions = useMemo(() => {
    if (!selectedShot || !selectedScene) return [];
    return shotsForSelectedScene
      .slice()
      .sort((a, b) => (a.scriptRange?.start ?? 0) - (b.scriptRange?.start ?? 0))
      .filter((s) => s.id !== selectedShot.id)
      .map((s) => ({
        id: s.id,
        label: getShotDisplayNumber(s.shotNumber, lang, shotPrefixMode),
      }));
  }, [shotsForSelectedScene, selectedShot, selectedScene, lang, shotPrefixMode]);

  const linkedWithId = useMemo(() => {
    if (!selectedShot?.linkGroupId) return "";

    const mates = shotsForSelectedScene
      .filter(
        (s) =>
          (s.linkGroupId || null) === selectedShot.linkGroupId &&
          s.id !== selectedShot.id
      )
      .sort((a, b) => (a.scriptRange?.start ?? 0) - (b.scriptRange?.start ?? 0));

    return mates[0]?.id || "";
  }, [selectedShot, shotsForSelectedScene]);

  const handleLinkPick = (e) => {
    const targetId = e.target.value;
    if (!selectedShot || !selectedScene) return;

    if (!targetId) {
      onUnlinkShot?.(selectedScene.id, selectedShot.id);
      return;
    }

    onLinkShots?.(selectedScene.id, selectedShot.id, targetId);
  };

  // ------------------------------
  // Sticky actions bar under title
  // ------------------------------
  useLayoutEffect(() => {
    const wrap = sceneDetailRef.current;
    const titleEl = sceneTitleRowRef.current;
    if (!wrap || !titleEl) return;

    const setVar = () => {
      const h = Math.round(titleEl.getBoundingClientRect().height || 0);
      wrap.style.setProperty("--sceneTitleRowH", `${h}px`);
    };

    setVar();

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(setVar);
    ro.observe(titleEl);

    return () => ro.disconnect();
  }, [displaySceneTitle, selectedSceneShotsCount, lang]);

  // ------------------------------
  // Scroll selected shot into view in list
  // ------------------------------
  useLayoutEffect(() => {
    if (!selectedShotId) return;

    const wrap = shotsWrapRef.current;
    const el = shotItemRefs.current.get(selectedShotId);
    if (!wrap || !el) return;

    const raf = requestAnimationFrame(() => {
      const wrapRect = wrap.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      const pad = 10;
      const topVisible = wrapRect.top + pad;
      const bottomVisible = wrapRect.bottom - pad;

      const fullyVisible = elRect.top >= topVisible && elRect.bottom <= bottomVisible;
      if (fullyVisible) return;

      const delta = elRect.top - topVisible;
      wrap.scrollTo({ top: Math.max(0, wrap.scrollTop + delta), behavior: "auto" });
    });

    return () => cancelAnimationFrame(raf);
  }, [selectedShotId, selectedSceneId, shotDisplayMode, shotsForSelectedScene.length]);

  // ------------------------------
  // Shot fields modal
  // ------------------------------
  const [isShotFieldModalOpen, setIsShotFieldModalOpen] = useState(false);

  // Éditeur de scène (menu ⋮) : ratio + format couleur par scène
  const [sceneEditorId, setSceneEditorId] = useState(null);
  const sceneBeingEdited = sceneEditorId
    ? scenes.find((s) => s.id === sceneEditorId) || null
    : null;

  // ------------------------------
  // Shot editor fields order
  // ------------------------------
  const editorFields = useMemo(() => {
    const defs = [
      {
        key: "shotSize",
        span: "half",
        render: () => (
          <div className="field">
            <label>{t("field.shotSize")}</label>
            <select
              value={normalizeTagValue("shotSize", selectedShot?.shotSize || "")}
              onChange={(e) =>
                onUpdateShot?.(selectedShot.id, { shotSize: e.target.value })
              }
            >
              {shotSizeOptions.map((opt) => (
                <option key={opt.value || "empty"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ),
      },
      {
        key: "angle",
        span: "half",
        render: () => (
          <div className="field">
            <label>{t("field.angle")}</label>
            <select
              value={normalizeTagValue(
                "angle",
                selectedShot?.angle || selectedShot?.shotType || ""
              )}
              onChange={(e) =>
                onUpdateShot?.(selectedShot.id, {
                  angle: e.target.value,
                  shotType: e.target.value, // legacy compat
                })
              }
            >
              {angleOptions.map((opt) => (
                <option key={opt.value || "empty"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ),
      },
      {
        key: "focus",
        span: "half",
        render: () => (
          <div className="field">
            <label>{t("field.focus")}</label>
            <select
              value={normalizeTagValue("focus", selectedShot?.focus || "")}
              onChange={(e) => onUpdateShot?.(selectedShot.id, { focus: e.target.value })}
            >
              {focusOptions.map((opt) => (
                <option key={opt.value || "empty"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ),
      },
      {
        key: "framing",
        span: "half",
        render: () => (
          <div className="field">
            <label>{t("field.framing")}</label>
            <select
              value={normalizeTagValue("framing", selectedShot?.framing || "")}
              onChange={(e) =>
                onUpdateShot?.(selectedShot.id, { framing: e.target.value })
              }
            >
              {framingOptions.map((opt) => (
                <option key={opt.value || "empty"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ),
      },
      {
        key: "movement",
        span: "half",
        render: () => (
          <div className="field">
            <label>{t("field.movement")}</label>
            <select
              value={normalizeTagValue("movement", selectedShot?.movement || "")}
              onChange={(e) =>
                onUpdateShot?.(selectedShot.id, { movement: e.target.value })
              }
            >
              {movementOptions.map((opt) => (
                <option key={opt.value || "empty"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ),
      },
      {
        key: "lensMm",
        span: "half",
        render: () => (
          <div className="field">
            <label>{t("field.lens")}</label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="---"
              value={selectedShot?.lensMm || ""}
              onChange={(e) => onUpdateShot?.(selectedShot.id, { lensMm: e.target.value })}
            />
          </div>
        ),
      },
      {
        key: "lights",
        span: "half",
        render: () => (
          <div className="field">
            <label>{t("field.lights")}</label>
            <input
              type="text"
              placeholder="---"
              value={selectedShot?.lights || ""}
              onChange={(e) => onUpdateShot?.(selectedShot.id, { lights: e.target.value })}
            />
          </div>
        ),
      },
      {
        key: "durationEstimateSec",
        span: "half",
        render: () => (
          <div className="field">
            <label>{t("field.estimation")}</label>
            <input
              type="number"
              min="0"
              value={selectedShot?.durationEstimateSec ?? 0}
              onChange={(e) =>
                onUpdateShot?.(selectedShot.id, {
                  durationEstimateSec: Number(e.target.value || 0),
                })
              }
            />
          </div>
        ),
      },
      {
        key: "linkedShot",
        span: "half",
        render: () => (
          <div className="field">
            <label>{t("field.linkedShot")}</label>
            <select value={linkedWithId} onChange={handleLinkPick}>
              <option value="">{t("linkedShot.none")}</option>
              {linkedShotOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ),
      },
    ];

    // tri selon layout (ordre global)
    return defs
      .slice()
      .sort((a, b) => (fieldIndex.get(a.key) ?? 999) - (fieldIndex.get(b.key) ?? 999));
  }, [
    t,
    selectedShot,
    onUpdateShot,
    shotSizeOptions,
    angleOptions,
    focusOptions,
    framingOptions,
    movementOptions,
    linkedWithId,
    linkedShotOptions,
    handleLinkPick,
    fieldIndex,
  ]);

  // ------------------------------
  // UI
  // ------------------------------
  return (
    <div
      className="panelInner"
      style={{ "--shot-frame-ratio": shotFrameCssRatio }}
    >
      <div className="panel2Header">
        <h2>{t("panel.scenes.title")}</h2>

        <div className="orgHeaderRight">
          {/* Invite quand le mode est actif mais rien n'est sélectionné */}
          {organizeMode && !orgTarget && (
            <div className="orgHint">{t("organize.hint")}</div>
          )}

          {/* Barre contextuelle : apparaît à gauche du bouton quand on organise */}
          {organizeMode && orgTarget === "shots" && (
            <div className="orgToolbar" role="toolbar" aria-label={t("organize.shots.toolbar")}>
              <button
                type="button"
                className={`btn btnGhost orgToolBtn ${orgMultiShots ? "isActive" : ""}`}
                onClick={() => setOrgMultiShots((v) => !v)}
                title={t("organize.multi")}
                aria-pressed={orgMultiShots ? "true" : "false"}
              >
                ☑ {t("organize.multi.short")}
              </button>
              <button
                type="button"
                className="btn btnGhost orgToolBtn"
                onClick={orgOpenRenumber}
                disabled={orgShotIds.length === 0}
                title={t("organize.shots.renumber")}
              >
                🔢
              </button>
              <button
                type="button"
                className="btn btnGhost orgToolBtn"
                onClick={() => orgReorderShots("up")}
                disabled={orgShotIds.length === 0}
                title={t("organize.shots.moveUpList")}
              >
                ▲
              </button>
              <button
                type="button"
                className="btn btnGhost orgToolBtn"
                onClick={() => orgReorderShots("down")}
                disabled={orgShotIds.length === 0}
                title={t("organize.shots.moveDownList")}
              >
                ▼
              </button>
              {moveTargetSceneId && (
                <button
                  type="button"
                  className="btn btnPrimary orgToolBtn orgMoveToSceneBtn"
                  onClick={orgMoveShotsToTarget}
                  disabled={orgShotIds.length === 0}
                  title={t("organize.shots.moveToScene")}
                >
                  ➜ {(() => {
                    const idx = scenes.findIndex((s) => s.id === moveTargetSceneId);
                    return idx >= 0 ? `SC${getSceneNumberLabel(scenes[idx], idx)}` : "";
                  })()}
                </button>
              )}
              <button
                type="button"
                className="btn btnDanger orgToolBtn"
                onClick={orgRequestDeleteShots}
                disabled={orgShotIds.length === 0}
                title={t("organize.shots.delete")}
              >
                🗑️
              </button>
            </div>
          )}

          {organizeMode && orgTarget === "scenes" && (
            <div className="orgToolbar" role="toolbar" aria-label={t("organize.scenes.toolbar")}>
              <button
                type="button"
                className="btn btnGhost orgToolBtn"
                onClick={orgStartRename}
                disabled={orgSceneIds.length !== 1}
                title={t("organize.scenes.rename")}
              >
                ✎
              </button>
              <button
                type="button"
                className={`btn btnGhost orgToolBtn ${orgMultiScenes ? "isActive" : ""}`}
                onClick={() => setOrgMultiScenes((v) => !v)}
                title={t("organize.multi")}
                aria-pressed={orgMultiScenes ? "true" : "false"}
              >
                ☑ {t("organize.multi.short")}
              </button>
              <button
                type="button"
                className="btn btnGhost orgToolBtn"
                onClick={() => orgMoveScenes("up")}
                disabled={orgSceneIds.length === 0}
                title={t("organize.scenes.moveUp")}
              >
                ▲
              </button>
              <button
                type="button"
                className="btn btnGhost orgToolBtn"
                onClick={() => orgMoveScenes("down")}
                disabled={orgSceneIds.length === 0}
                title={t("organize.scenes.moveDown")}
              >
                ▼
              </button>
              <button
                type="button"
                className="btn btnDanger orgToolBtn"
                onClick={orgRequestToggleDeleteScenes}
                disabled={orgSceneIds.length === 0}
                title={t("organize.scenes.delete")}
              >
                🗑️
              </button>
            </div>
          )}

          <button
            type="button"
            className={`btn organizeToggle ${organizeMode ? "isActive" : ""}`}
            onClick={() => setOrganizeMode?.((v) => !v)}
            aria-pressed={organizeMode ? "true" : "false"}
            title={t("organize.toggle.title")}
          >
            ⇅ {t("organize.toggle")}
          </button>
        </div>
      </div>

      {!scriptText.trim() ? (
        <div className="emptyState">
          <div className="emptyTitle">{t("scenes.empty.noScript.title")}</div>
          <div className="emptyText">{t("scenes.empty.noScript.text")}</div>
        </div>
      ) : scenes.length === 0 ? (
        <div className="emptyState">
          <div className="emptyTitle">{t("scenes.empty.noScenes.title")}</div>
          <div className="emptyText">{t("scenes.empty.noScenes.text")}</div>
        </div>
      ) : (
        <div className="scenePanelBody">
          {/* -------- Scene detail (droite) -------- */}
          <div className="sceneDetail" ref={sceneDetailRef}>
            {!selectedScene ? (
              <div className="emptyState">
                <div className="emptyTitle">{t("scenes.empty.selectScene")}</div>
              </div>
            ) : (
              <>
                <div className="sceneDetailHeader">
                  {/* sticky top */}
                  <div className="sceneTitleRow" ref={sceneTitleRowRef}>
                    <div className="sceneDetailTitle">{displaySceneTitle}</div>

                    <span className="badge badgeShots">
                      {t("badge.shots", { count: selectedSceneShotsCount })}
                    </span>
                  </div>

                  {/* sticky under title row */}
                  <div className="sceneDetailActionsSticky">
                    <div className="sceneDetailActions">
                      <div className="addShotSplit" ref={addMenuRef}>
                        <button
                          className="btn btnPrimary addShotMainBtn"
                          onClick={handleAddShot}
                          disabled={!canAddShot}
                          type="button"
                        >
                          {t("scenes.addShot")}
                        </button>
                        <button
                          className="btn btnPrimary addShotCaretBtn"
                          type="button"
                          disabled={!selectedScene}
                          aria-haspopup="menu"
                          aria-expanded={isAddMenuOpen}
                          title={t("scenes.addShot.more")}
                          onClick={() => setIsAddMenuOpen((v) => !v)}
                        >
                          ▾
                        </button>

                        {isAddMenuOpen && (
                          <div className="addShotMenu" role="menu">
                            <button
                              className="addShotMenuItem"
                              type="button"
                              role="menuitem"
                              disabled={!selectedScene}
                              onClick={handleAddShotNoText}
                            >
                              {t("scenes.addShotNoText")}
                            </button>
                          </div>
                        )}
                      </div>

                      <button
                        className={`btn ${isConfirmingTextUpdate ? "btnPrimary" : ""}`}
                        onClick={handleModifyText}
                        disabled={!canModifyText}
                        type="button"
                        title={isConfirmingTextUpdate ? t("scenes.confirm") : t("scenes.updateText")}
                      >
                        {isConfirmingTextUpdate ? t("scenes.confirm") : t("scenes.updateText")}
                      </button>

                      {/* Réordonnancement des plans déplacé dans le mode Organiser. */}

                      {/* menu mode d’affichage */}
                      <div className="modeMenuWrap rightAction" ref={modeMenuRef}>
                        <button
                          className="btn btnGhost"
                          type="button"
                          onClick={() => setIsShotFieldModalOpen(true)}
                          title={t("shotFields.open") || "Personnaliser champs & tags"}
                        >
                          {t("shotFields.button") || "Champs"}
                        </button>

                        <button
                          className="btn btnGhost modeMenuButton"
                          type="button"
                          onClick={() => setIsModeMenuOpen((v) => !v)}
                          aria-haspopup="menu"
                          aria-expanded={isModeMenuOpen}
                        >
                          <span className="modeMenuLabel">
                            {DISPLAY_MODES[shotDisplayMode]?.label || DISPLAY_MODES.classic.label}
                          </span>
                          <span className="modeMenuChevron">▾</span>
                        </button>

                        {isModeMenuOpen && (
                          <div className="modeMenuPanel" role="menu">
                            {Object.entries(DISPLAY_MODES).map(([key, meta]) => {
                              const active = key === shotDisplayMode;
                              return (
                                <button
                                  key={key}
                                  type="button"
                                  className={`modeMenuItem ${active ? "active" : ""}`}
                                  role="menuitemradio"
                                  aria-checked={active}
                                  onClick={() => {
                                    setShotDisplayMode(key);
                                    setIsModeMenuOpen(false);
                                  }}
                                >
                                  <span className="modeMenuItemLeft">
                                    <span className="modeMenuItemTitle">{meta.label}</span>
                                    <span className="modeMenuItemSub">{meta.sub}</span>
                                  </span>
                                  <span className="modeMenuItemRight">{active ? "✓" : ""}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {isConfirmingTextUpdate && (
                      <div className="confirmInline">
                        <span>{t("scenes.confirmInline")}</span>
                        <button
                          className="btn btnGhost"
                          type="button"
                          onClick={() => setConfirmTextUpdate(null)}
                        >
                          {t("scenes.cancel")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* -------- Shots list -------- */}
                <div className="sceneDetailScroll" ref={shotsWrapRef}>
                  <div className="sceneShotsBlock">
                    <input
                      ref={thumbImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={onThumbImagePicked}
                      style={{ display: "none" }}
                    />

                    {shotsForSelectedScene.length === 0 ? (
                      <div className="emptyText">
                        {t("shots.none")}
                        <p>{t("shots.none.step1")}</p>
                        <p>{t("shots.none.step2")}</p>
                        <p>{t("shots.none.step3")}</p>
                      </div>
                    ) : (
                      <div className="shotList">
                        {shotsForSelectedScene.map((shot) => {
                          const isSelected = shot.id === selectedShotId;
                          const imgSrc = getShotImageSrc(shot);

                          const showAssignedText = shotDisplayMode !== "big";
                          const showThumbRight = shotDisplayMode === "classic";
                          const showThumbBig = shotDisplayMode === "big";

                          // format effectif (override plan -> scène -> global)
                          const shotFrameCss = getShotFrameFormat(
                            effectiveFrameFormatValue(shot, selectedScene, shotFrameFormat)
                          ).css;
                          const shotColorFilter = getColorFormatFilter(
                            effectiveColorFormatValue(shot, selectedScene)
                          );

                          return (
                            <button
                              key={shot.id}
                              ref={(el) => {
                                if (!el) shotItemRefs.current.delete(shot.id);
                                else shotItemRefs.current.set(shot.id, el);
                              }}
                              className={`shotItem ${isSelected ? "active" : ""} shotItem--${shotDisplayMode} ${
                                dragShotId === shot.id ? "isDragging" : ""
                              } ${dropTargetId === shot.id ? "isDropTarget" : ""} ${
                                organizeMode ? "organizeMode" : ""
                              } ${orgShotIds.includes(shot.id) ? "orgSelected" : ""}`}
                              onClick={() => {
                                if (organizeMode) {
                                  handleShotOrgClick(shot.id);
                                  return;
                                }
                                setSelectedShotId(shot.id);
                              }}
                              onDoubleClick={() => {
                                if (organizeMode) return;
                                setSelectedShotId(shot.id);
                                setIsShotEditorOpen(true);
                              }}
                              draggable={!organizeMode}
                              onDragStart={(e) => {
                                setDragShotId(shot.id);
                                e.dataTransfer.effectAllowed = "move";
                                // ✅ requis (notamment Firefox) pour que le drag démarre vraiment
                                try {
                                  e.dataTransfer.setData("text/plain", shot.id);
                                } catch {}
                              }}
                              onDragOver={(e) => {
                                if (dragShotId && dragShotId !== shot.id) {
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = "move";
                                  if (dropTargetId !== shot.id) setDropTargetId(shot.id);
                                }
                              }}
                              onDragLeave={() => {
                                if (dropTargetId === shot.id) setDropTargetId(null);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                const dragId =
                                  dragShotId || e.dataTransfer.getData("text/plain");
                                if (dragId && dragId !== shot.id && selectedScene) {
                                  const dragShot = shots.find((s) => s.id === dragId);
                                  dispatchReorder(dragShot, "drop", shot.id);
                                }
                                setDragShotId(null);
                                setDropTargetId(null);
                              }}
                              onDragEnd={() => {
                                setDragShotId(null);
                                setDropTargetId(null);
                              }}
                              type="button"
                            >
                              {organizeMode && orgTarget === "shots" && orgMultiShots && (
                                <span
                                  className={`orgCheck ${
                                    orgShotIds.includes(shot.id) ? "isChecked" : ""
                                  }`}
                                  aria-hidden="true"
                                >
                                  {orgShotIds.includes(shot.id) ? "☑" : "☐"}
                                </span>
                              )}
                              <div className="shotItemTop">
                                {/* numéro + dot à gauche (inchangé selon mode) */}
                                <div className="shotNumCol">
                                  <div className="shotNum">
                                    {getShotDisplayNumber(shot.shotNumber, lang, shotPrefixMode)}
                                  </div>
                                  <span
                                    className="shotColorDot"
                                    style={{ background: shot.highlightColor || DEFAULT_HIGHLIGHT }}
                                    title={shot.highlightColor || DEFAULT_HIGHLIGHT}
                                  />
                                </div>

                                {/* BIG: image à gauche, infos à droite */}
                                {showThumbBig ? (
                                  <>
                                    <div className="shotThumb big left" style={{ aspectRatio: shotFrameCss }}>
                                      <div className="shotThumbActions">
                                        <span
                                          className="shotThumbAction"
                                          role="button"
                                          tabIndex={0}
                                          title={t("image.import")}
                                          onClick={(e) => openThumbImagePicker(shot.id, e)}
                                        >
                                          📥
                                        </span>

                                        <span
                                          className={`shotThumbAction ${imgSrc ? "" : "disabled"}`}
                                          role="button"
                                          tabIndex={0}
                                          title={t("image.remove")}
                                          onClick={(e) => imgSrc && removeThumbImage(shot.id, e)}
                                        >
                                          🗑️
                                        </span>
                                      </div>

                                      {imgSrc ? (
                                        <img
                                          src={imgSrc}
                                          alt=""
                                          draggable="false"
                                          style={{ filter: shotColorFilter }}
                                        />
                                      ) : (
                                        <div className="shotThumbPlaceholder" />
                                      )}
                                    </div>

                                    <div className="shotBigInfo">
                                      <div className="shotInfoLine">
                                        {(() => {
                                          const tags = getShotInfoTagNodes(shot);
                                          return tags.length ? tags : (
                                            <span className="shotTag shotTag--empty">—</span>
                                          );
                                        })()}
                                      </div>

                                      <div className="shotDescLine">{getShotDescriptionLine(shot)}</div>
                                    </div>
                                  </>
                                ) : (
                                  // CLASSIC/COMPACT
                                  <div className="shotItemMain">
                                    <div className="shotItemContent">
                                      <div className="shotInfoLine">
                                        {(() => {
                                          const tags = getShotInfoTagNodes(shot);
                                          return tags.length ? tags : (
                                            <span className="shotTag shotTag--empty">—</span>
                                          );
                                        })()}
                                      </div>

                                      <div className="shotDescLine">{getShotDescriptionLine(shot)}</div>

                                      {showAssignedText && (
                                        <div className="shotTextLine">{getAssignedTextPreview(shot)}</div>
                                      )}
                                    </div>

                                    {showThumbRight ? (
                                      <div className="shotThumb" style={{ aspectRatio: shotFrameCss }}>
                                        <div className="shotThumbActions">
                                          <span
                                            className="shotThumbAction"
                                            role="button"
                                            tabIndex={0}
                                            title={t("image.import")}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }}
                                            onClick={(e) => openThumbImagePicker(shot.id, e)}
                                          >
                                            📥
                                          </span>

                                          <span
                                            className={`shotThumbAction ${imgSrc ? "" : "disabled"}`}
                                            role="button"
                                            tabIndex={0}
                                            title={t("image.remove")}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }}
                                            onClick={(e) => imgSrc && removeThumbImage(shot.id, e)}
                                          >
                                            🗑️
                                          </span>
                                        </div>

                                        {imgSrc ? (
                                          <img
                                          src={imgSrc}
                                          alt=""
                                          draggable="false"
                                          style={{ filter: shotColorFilter }}
                                        />
                                        ) : (
                                          <div className="shotThumbPlaceholder" />
                                        )}
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* ✅ Shot Editor (fixé en bas, ou déporté dans le panneau scénario) */}
                {(() => {
                const shotEditorBlock = (
                <div
                  className={`shotEditorBlock ${
                    editorInScriptPanel && editorHost ? "shotEditorHosted" : "shotEditorBlockFixed"
                  } ${isShotEditorOpen ? "isOpen" : "isClosed"}`}
                >
                  <div
                    className="shotEditorHeaderRow"
                    onDoubleClick={() => {
                      if (!(editorInScriptPanel && editorHost)) setIsShotEditorOpen((v) => !v);
                    }}
                    title={isShotEditorOpen ? "" : t("shotEditor.expandHint")}
                  >
                    <div className="shotEditorHeaderLeft">
                      <div className="sceneShotEditor">{t("shotEditor.title")}</div>
                    </div>

                    {/* ✅ #2 : pas de bouton de repli quand l'éditeur est déporté sur le scénario */}
                    {!(editorInScriptPanel && editorHost) && (
                      <button
                        className="btn btnGhost shotEditorToggleBtn"
                        type="button"
                        onClick={() => setIsShotEditorOpen((v) => !v)}
                        aria-label={isShotEditorOpen ? "Collapse Shot Editor" : "Expand Shot Editor"}
                      >
                        {isShotEditorOpen ? "▾" : "▴"}
                      </button>
                    )}
                  </div>

                  {(isShotEditorOpen || (editorInScriptPanel && editorHost)) && (
                    <div className="shotEditorBody">
                      {!selectedShot ? (
                        <div className="emptyText">{t("shotEditor.empty")}</div>
                      ) : (
                        <>
                          {/* Description + Color (fixe) */}
                          <div className="shotEditorTopRow">
                            <div className="shotEditorTopDesc">
                              <textarea
                                className="shotDescription"
                                rows={3}
                                value={selectedShot.description || ""}
                                onChange={(e) =>
                                  onUpdateShot?.(selectedShot.id, { description: e.target.value })
                                }
                                placeholder={t("shotEditor.desc.placeholder")}
                              />
                            </div>

                            <div className="field shotEditorTopHighlight">
                              <div className="colorRow colorRowCompact">
                                <input
                                  className="colorInput"
                                  type="color"
                                  value={selectedShot.highlightColor?.trim() || DEFAULT_HIGHLIGHT}
                                  onChange={(e) =>
                                    onUpdateShot?.(selectedShot.id, { highlightColor: e.target.value })
                                  }
                                  title={t("shotEditor.color.choose")}
                                />

                                <button
                                  className="btn btnGhost"
                                  type="button"
                                  onClick={() => onUpdateShot?.(selectedShot.id, { highlightColor: "" })}
                                  title={t("shotEditor.color.revert")}
                                >
                                  {t("shotEditor.color.default")}
                                </button>

                                <div className="swatches">
                                  {HIGHLIGHT_PRESETS.map((c) => (
                                    <button
                                      key={c}
                                      type="button"
                                      className="swatchBtn"
                                      style={{ background: c }}
                                      onClick={() => onUpdateShot?.(selectedShot.id, { highlightColor: c })}
                                      title={c}
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* MASTER + Image */}
                          <div className="shotEditorMetaRow">
                            <button
                              type="button"
                              className={`masterToggleBtn ${selectedShot.isMaster ? "isMaster" : ""}`}
                              onClick={() =>
                                onUpdateShot?.(selectedShot.id, { isMaster: !selectedShot.isMaster })
                              }
                              aria-pressed={selectedShot.isMaster ? "true" : "false"}
                              title={t("shotEditor.master.title")}
                            >
                              <span aria-hidden="true">★</span> {t("shotEditor.master")}
                            </button>

                            {/* Bug 6 : import image accessible quel que soit le mode d'affichage */}
                            <div className="shotEditorImage">
                              <span className="shotEditorImageLabel">{t("field.image")}</span>
                              {getShotImageSrc(selectedShot) ? (
                                <img
                                  className="shotEditorImageThumb"
                                  src={getShotImageSrc(selectedShot)}
                                  alt=""
                                  draggable="false"
                                />
                              ) : (
                                <span className="shotEditorImageThumb shotEditorImageThumb--empty" />
                              )}
                              <button
                                type="button"
                                className="btn btnGhost"
                                onClick={(e) => openThumbImagePicker(selectedShot.id, e)}
                                title={t("image.import")}
                              >
                                📥
                              </button>
                              <button
                                type="button"
                                className={`btn btnGhost ${getShotImageSrc(selectedShot) ? "" : "isDisabled"}`}
                                onClick={(e) =>
                                  getShotImageSrc(selectedShot) && removeThumbImage(selectedShot.id, e)
                                }
                                title={t("image.remove")}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>

                          {/* Format cadre + couleur (override individuel du plan) */}
                          <div className="shotEditorFieldsGrid">
                            <div className="shotEditorFieldWrap">
                              <div className="field">
                                <label>{t("field.ratio")}</label>
                                <select
                                  value={selectedShot.frameFormat || ""}
                                  onChange={(e) =>
                                    onUpdateShot?.(selectedShot.id, {
                                      frameFormat: e.target.value || "",
                                    })
                                  }
                                >
                                  <option value="">{t("shotEditor.inheritScene")}</option>
                                  {SHOT_FRAME_FORMATS.map((fmt) => (
                                    <option key={fmt.value} value={fmt.value}>
                                      {fmt.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <div className="shotEditorFieldWrap">
                              <div className="field">
                                <label>{t("field.colorFormat")}</label>
                                <select
                                  value={selectedShot.colorFormat || ""}
                                  onChange={(e) =>
                                    onUpdateShot?.(selectedShot.id, {
                                      colorFormat: e.target.value || "",
                                    })
                                  }
                                >
                                  <option value="">{t("shotEditor.inheritScene")}</option>
                                  {SHOT_COLOR_FORMATS.map((cf) => (
                                    <option key={cf.value} value={cf.value}>
                                      {t(cf.labelKey)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>

                          {/* Champs réordonnables */}
                          <div className="shotEditorFieldsGrid">
                            {editorFields.map((f) => (
                              <div
                                key={f.key}
                                className={`shotEditorFieldWrap ${f.span === "full" ? "isFull" : ""}`}
                              >
                                {f.render()}
                              </div>
                            ))}
                          </div>

                          {/* Texte assigné + actions */}
                          <div className="BottomShotEditorBlock">
                            <div className="titleTexteAssign">{t("shotEditor.assigned")}</div>
                            <pre className="shotAssignedText">{selectedShotText.trim()}</pre>

                            <div style={{ marginTop: 10 }}>
                              <div className={`shotDeleteRow ${isConfirmingReset || isConfirmingDelete ? "hasConfirm" : ""}`}>
                                {/* Delete */}
                                <button
                                  className={`btn ${isConfirmingDelete ? "btnDanger" : "btnGhost"}`}
                                  type="button"
                                  onClick={handleDeleteShot}
                                  title={
                                    isConfirmingDelete
                                      ? t("shotEditor.delete.confirmTitle")
                                      : t("shotEditor.delete.title")
                                  }
                                >
                                  {isConfirmingDelete ? t("shotEditor.delete.confirm") : t("shotEditor.delete")}
                                </button>

                                {/* Supprimer texte assigné */}
                                <button
                                  className={`btn ${isConfirmingClearText ? "btnDanger" : "btnGhost"}`}
                                  type="button"
                                  onClick={handleClearText}
                                  title={
                                    isConfirmingClearText
                                      ? t("shotEditor.clearText.confirmTitle")
                                      : t("shotEditor.clearText.title")
                                  }
                                >
                                  {isConfirmingClearText
                                    ? t("shotEditor.clearText.confirm")
                                    : t("shotEditor.clearText")}
                                </button>

                                {/* Reset */}
                                <button
                                  className={`btn ${isConfirmingReset ? "btnDanger" : "btnGhost"}`}
                                  type="button"
                                  onClick={handleResetShot}
                                  title={
                                    isConfirmingReset
                                      ? t("shotEditor.reset.confirmTitle")
                                      : t("shotEditor.reset.title")
                                  }
                                >
                                  {isConfirmingReset ? t("shotEditor.reset.confirm") : t("shotEditor.reset")}
                                </button>

                                {/* Cancel */}
                                {(isConfirmingReset || isConfirmingDelete || isConfirmingClearText) && (
                                  <button
                                    className="btn btnGhost"
                                    type="button"
                                    onClick={() => {
                                      setConfirmResetShot(null);
                                      setConfirmDeleteShot(null);
                                      setConfirmClearText(null);
                                    }}
                                  >
                                    {t("scenes.cancel")}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                );
                return editorInScriptPanel && editorHost
                  ? createPortal(shotEditorBlock, editorHost)
                  : shotEditorBlock;
                })()}
              </>
            )}
          </div>

          {/* -------- Scene list (gauche) -------- */}
          <div className="sceneList">
            {scenes.map((scene, idx) => {
              const isActive = scene.id === selectedSceneId;
              const count = shotsBySceneId.get(scene.id) ?? 0;
              const totalSec = durationBySceneId.get(scene.id) ?? 0;

              return (
                <button
                  key={scene.id}
                  className={`sceneItem ${isActive ? "active" : ""} ${
                    organizeMode ? "organizeMode" : ""
                  } ${orgSceneIds.includes(scene.id) ? "orgSelected" : ""} ${
                    moveTargetSceneId === scene.id ? "orgMoveTarget" : ""
                  } ${scene.deleted ? "sceneDeleted" : ""}`}
                  onClick={() => {
                    if (organizeMode) {
                      handleSceneOrgClick(scene.id);
                      return;
                    }
                    setSelectedSceneId(scene.id);
                    setSelectedShotId(null);
                  }}
                  title={scene.title}
                  type="button"
                >
                  {organizeMode && orgTarget === "scenes" && orgMultiScenes && (
                    <span
                      className={`orgCheck orgCheckScene ${
                        orgSceneIds.includes(scene.id) ? "isChecked" : ""
                      }`}
                      aria-hidden="true"
                    >
                      {orgSceneIds.includes(scene.id) ? "☑" : "☐"}
                    </span>
                  )}
                  <span
                    className="sceneMenuBtn"
                    role="button"
                    tabIndex={0}
                    title={t("sceneEditor.open")}
                    aria-label={t("sceneEditor.open")}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSceneEditorId(scene.id);
                    }}
                  >
                    ⋮
                  </span>

                  <div className="sceneItemTop">
                    <div className="sceneIndex">SC{getSceneNumberLabel(scene, idx)}</div>
                    <div className="sceneItemMeta">
                      <span>
                        🕒 {t("totals.time")} : {formatDuration(totalSec)}
                      </span>
                      <span>🎞️ {t("badge.shots", { count })}</span>
                    </div>
                  </div>

                  <div className="sceneTitle">
                    {scene.title || t("fallback.scene", { n: idx + 1 })}
                  </div>
                </button>
              );
            })}

            {/* TOTAL sticky */}
            <div className="sceneTotals">
              <div className="sceneTotalsWrap" ref={totalsWrapRef}>
                {/* Visible row */}
                <div className={`sceneTotalsRow ${totalsStacked ? "isStacked" : "isCompact"}`}>
                  <div className="sceneTotalsItem">
                    <div className="sceneTotalsItemIcon" aria-hidden="true">
                      🎦
                    </div>
                    <div className="sceneTotalsItemTitle">{t("totals.scenes")}</div>
                    <div className="sceneTotalsItemValue">{totalScenes}</div>
                  </div>

                  <div className="sceneTotalsItem">
                    <div className="sceneTotalsItemIcon" aria-hidden="true">
                      🕒
                    </div>
                    <div className="sceneTotalsItemTitle">{t("totals.time")}</div>
                    <div className="sceneTotalsItemValue">{formatDuration(totalDurationSec)}</div>
                  </div>

                  <div className="sceneTotalsItem">
                    <div className="sceneTotalsItemIcon" aria-hidden="true">
                      🎞️
                    </div>
                    <div className="sceneTotalsItemTitle">{t("totals.shots")}</div>
                    <div className="sceneTotalsItemValue">{totalShots}</div>
                  </div>
                </div>

                {/* Hidden measurer */}
                <div
                  ref={totalsMeasureRef}
                  className="sceneTotalsRow sceneTotalsMeasure isCompact"
                  aria-hidden="true"
                >
                  <div className="sceneTotalsItem">
                    <div className="sceneTotalsItemIcon" aria-hidden="true">
                      🎦
                    </div>
                    <div className="sceneTotalsItemTitle">{t("totals.scenes")}</div>
                    <div className="sceneTotalsItemValue">{totalScenes}</div>
                  </div>

                  <div className="sceneTotalsItem">
                    <div className="sceneTotalsItemIcon" aria-hidden="true">
                      🕒
                    </div>
                    <div className="sceneTotalsItemTitle">{t("totals.time")}</div>
                    <div className="sceneTotalsItemValue">{formatDuration(totalDurationSec)}</div>
                  </div>

                  <div className="sceneTotalsItem">
                    <div className="sceneTotalsItemIcon" aria-hidden="true">
                      🎞️
                    </div>
                    <div className="sceneTotalsItemTitle">{t("totals.shots")}</div>
                    <div className="sceneTotalsItemValue">{totalShots}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modale layout */}
          <ShotFieldLayoutModal
            open={isShotFieldModalOpen}
            onClose={() => setIsShotFieldModalOpen(false)}
            layout={effectiveFieldLayout}
            setLayout={setShotFieldLayout}
            t={t}
          />

          {/* Éditeur de scène : ratio + format couleur (appliqués à tous les plans de la scène) */}
          {sceneBeingEdited && (
            <div
              className="modalOverlay"
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setSceneEditorId(null);
              }}
            >
              <div className="modalCard modalW_settings" onMouseDown={(e) => e.stopPropagation()}>
                <button
                  className="modalCloseButton"
                  type="button"
                  onClick={() => setSceneEditorId(null)}
                  title={t("common.close")}
                >
                  ✕
                </button>

                <h3 className="modalTitle">
                  {t("sceneEditor.title", {
                    title:
                      `SC${getSceneNumberLabel(
                        sceneBeingEdited,
                        scenes.findIndex((s) => s.id === sceneBeingEdited.id)
                      )}`,
                  })}
                </h3>

                <div className="modalBody">
                  <div className="storyPdfHint">{t("sceneEditor.help")}</div>

                  <div className="storyPdfRow" style={{ marginTop: 12 }}>
                    <label className="storyPdfLabel">{t("settings.frameFormat")}</label>
                    <select
                      className="selectLanguage"
                      value={sceneBeingEdited.frameFormat || ""}
                      onChange={(e) =>
                        onUpdateScene?.(sceneBeingEdited.id, {
                          frameFormat: e.target.value || undefined,
                        })
                      }
                    >
                      <option value="">{t("sceneEditor.inherit")}</option>
                      {SHOT_FRAME_FORMATS.map((fmt) => (
                        <option key={fmt.value} value={fmt.value}>
                          {fmt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="storyPdfRow" style={{ marginTop: 12 }}>
                    <label className="storyPdfLabel">{t("field.colorFormat")}</label>
                    <select
                      className="selectLanguage"
                      value={sceneBeingEdited.colorFormat || DEFAULT_SHOT_COLOR_FORMAT}
                      onChange={(e) =>
                        onUpdateScene?.(sceneBeingEdited.id, { colorFormat: e.target.value })
                      }
                    >
                      {SHOT_COLOR_FORMATS.map((cf) => (
                        <option key={cf.value} value={cf.value}>
                          {t(cf.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="modalFooter">
                  <button className="btn btnPrimary" type="button" onClick={() => setSceneEditorId(null)}>
                    {t("common.close")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation de réordonnancement (renumérotation) (#4) */}
          {reorderConfirm && (
            <div
              className="modalOverlay"
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setReorderConfirm(null);
              }}
            >
              <div className="modalCard modalW_refresh" onMouseDown={(e) => e.stopPropagation()}>
                <h3 className="modalTitle">{t("shots.reorder.confirmTitle")}</h3>
                <div className="modalBody">
                  <p>{t("shots.reorder.confirmText")}</p>
                  <div className="modalFooter">
                    <button className="btn" type="button" onClick={() => setReorderConfirm(null)}>
                      {t("scenes.cancel")}
                    </button>
                    <button className="btn btnPrimary" type="button" onClick={confirmMoveSelectedShot}>
                      {t("shots.reorder.confirm")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Alerte : plan lié qui passe au-dessus de son -A / master */}
          {sautConfirm && (
            <div
              className="modalOverlay"
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setSautConfirm(null);
              }}
            >
              <div className="modalCard modalW_refresh" onMouseDown={(e) => e.stopPropagation()}>
                <h3 className="modalTitle">{t("shots.aboveAnchor.title")}</h3>
                <div className="modalBody">
                  <p>{t("shots.aboveAnchor.text")}</p>
                  <div className="modalFooter">
                    <button className="btn" type="button" onClick={() => setSautConfirm(null)}>
                      {t("shots.aboveAnchor.no")}
                    </button>
                    <button className="btn btnPrimary" type="button" onClick={confirmSautYes}>
                      {t("shots.aboveAnchor.yes")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation de suppression en mode organisation */}
          {orgDeleteConfirm && (
            <div
              className="modalOverlay"
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setOrgDeleteConfirm(null);
              }}
            >
              <div className="modalCard modalW_refresh" onMouseDown={(e) => e.stopPropagation()}>
                <h3 className="modalTitle">{t("organize.delete.confirmTitle")}</h3>
                <div className="modalBody">
                  <p>
                    {orgDeleteConfirm.kind === "shots"
                      ? t("organize.delete.confirmShots", { count: orgDeleteConfirm.count })
                      : t("organize.delete.confirmScenes", { count: orgDeleteConfirm.count })}
                  </p>
                  <div className="modalFooter">
                    <button className="btn" type="button" onClick={() => setOrgDeleteConfirm(null)}>
                      {t("scenes.cancel")}
                    </button>
                    <button className="btn btnDanger" type="button" onClick={orgConfirmDelete}>
                      {t("organize.delete.confirm")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Renommer une scène (mode organisation) */}
          {renameSceneId && (
            <div
              className="modalOverlay"
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setRenameSceneId(null);
              }}
            >
              <div className="modalCard modalW_refresh" onMouseDown={(e) => e.stopPropagation()}>
                <h3 className="modalTitle">{t("organize.scenes.rename")}</h3>
                <div className="modalBody">
                  <input
                    type="text"
                    className="sceneRenameInput"
                    value={renameDraft}
                    autoFocus
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitRename();
                      } else if (e.key === "Escape") {
                        setRenameSceneId(null);
                      }
                    }}
                  />
                  <div className="modalFooter">
                    <button className="btn" type="button" onClick={() => setRenameSceneId(null)}>
                      {t("scenes.cancel")}
                    </button>
                    <button className="btn btnPrimary" type="button" onClick={commitRename}>
                      {t("organize.scenes.rename.apply")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Renumérotation des plans (mode organisation) */}
          {renumberOpen && (
            <div
              className="modalOverlay"
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setRenumberOpen(false);
              }}
            >
              <div className="modalCard modalW_refresh" onMouseDown={(e) => e.stopPropagation()}>
                <h3 className="modalTitle">{t("organize.shots.renumber.title")}</h3>
                <div className="modalBody">
                  <div className="helpText">{t("organize.shots.renumber.help")}</div>

                  <div className="renumberList">
                    {renumberOrder.map((id, idx) => {
                      const shot = shots.find((s) => s.id === id);
                      if (!shot) return null;
                      const desc = (shot.description || "").trim();
                      return (
                        <div className="renumberRow" key={id}>
                          <span className="renumberPos">{idx + 1}</span>
                          <span className="renumberLabel">
                            <span className="renumberOldNum">
                              {getShotDisplayNumber(shot.shotNumber, lang, shotPrefixMode)}
                            </span>
                            <span className="renumberDesc">{desc || "—"}</span>
                          </span>
                          <span className="renumberCtrls">
                            <button
                              className="btn btnGhost"
                              type="button"
                              disabled={idx === 0}
                              onClick={() => renumberMove(idx, "up")}
                              title={t("organize.shots.moveUpList")}
                            >
                              ↑
                            </button>
                            <button
                              className="btn btnGhost"
                              type="button"
                              disabled={idx === renumberOrder.length - 1}
                              onClick={() => renumberMove(idx, "down")}
                              title={t("organize.shots.moveDownList")}
                            >
                              ↓
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="modalFooter">
                    <button className="btn" type="button" onClick={() => setRenumberOpen(false)}>
                      {t("scenes.cancel")}
                    </button>
                    <button className="btn btnPrimary" type="button" onClick={renumberApply}>
                      {t("organize.shots.renumber.apply")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
